import React, { useState, useEffect, useRef } from "react";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { Helmet } from 'react-helmet';

gsap.registerPlugin(ScrollTrigger);

function OurStudioClassesSection({ ImageUrls = [], VideoUrls = [] }) {
  const imageData = [
    {
      src:
        "https://cdn.prod.website-files.com/5eea1fd06e21541535c81901/6256ebd82354f3764b2a5c18_Kids-5.jpeg",
      alt: "kids",
      text: "KIDS",
      classKey: "kids",
      content: "Content for Kids Classes: Fun and engaging dance classes for children of all ages, focusing on creativity and movement.",
    },
    {
      src:
        "https://i.pinimg.com/236x/13/db/38/13db3809d9bdf410de0ffcf8e4012533.jpg",
      alt: "adult",
      text: "ADULT",
      classKey: "adult",
      content: "Content for Adult Classes: Diverse range of dance styles for adults, from beginner to advanced levels.",
    },
    {
      src:
        "https://www.shutterstock.com/image-photo/athlete-asian-sportswoman-jumping-dance-600nw-2184753393.jpg",
      alt: "fitness",
      text: "FITNESS",
      classKey: "fitness",
      content: "Content for Fitness Classes: High-energy dance fitness classes to improve stamina, flexibility, and strength.",
    },
    {
      src:
        "https://static.wixstatic.com/media/c8c956_4a9ae9ec04644ec6890c4dd198952cce~mv2.jpeg/v1/fill/w_980,h_1307,al_c,q_85,usm_0.66_1.00_0.01,enc_avif,quality_auto/c8c956_4a9ae9ec04644ec6890c4dd198952cce~mv2.jpeg",
      alt: "online",
      text: "ONLINE",
      classKey: "online",
      content: "Content for Online Classes: Learn from anywhere with our comprehensive online dance courses and live sessions.",
    },
    {
      src:
        "https://islarosedanceacademy.com/wp-content/uploads/2023/07/IRDA-Clases-Privadas.jpg",
      alt: "private",
      text: "PRIVATE",
      classKey: "private",
      content: "Content for Private Classes: Personalized one-on-one dance instruction tailored to your specific goals and pace.",
    },
  ];

  const containerRef = useRef(null);
  const imageCardRefs = useRef([]);
  const svgPathRefs = useRef([]);
  const [selectedClassContent, setSelectedClassContent] = useState(null);

  useEffect(() => {
    const ctx = gsap.context(() => {
      const container = containerRef.current;
      const cards = imageCardRefs.current.filter(Boolean);

      gsap.fromTo(
        cards,
        { opacity: 0, y: 40, scale: 0.98 },
        {
          opacity: 1,
          y: 0,
          scale: 1,
          duration: 0.7,
          ease: "power3.out",
          stagger: 0.12,
          scrollTrigger: {
            trigger: container,
            start: "top 85%",
            toggleActions: "play none none none",
          },
        }
      );

      svgPathRefs.current.forEach((p, i) => {
        if (!p) return;

        const shapes = [
          p.getAttribute("data-shape-a"),
          p.getAttribute("data-shape-b"),
        ].filter(Boolean);

        if (shapes.length === 2) {
          gsap.to(p, {
            duration: 18 + i * 4,
            attr: { d: shapes[1] },
            ease: "sine.inOut",
            repeat: -1,
            yoyo: true,
          });
        }

        const dashLen = 1600;
        p.style.strokeDasharray = dashLen;
        gsap.to(p, {
          strokeDashoffset: -dashLen,
          duration: 24 + i * 6,
          ease: "linear",
          repeat: -1,
        });

        gsap.to(p, {
          y: i % 2 === 0 ? -8 : 8,
          duration: 10 + i * 3,
          ease: "sine.inOut",
          repeat: -1,
          yoyo: true,
        });
      });
    }, containerRef);

    return () => ctx.revert();
  }, []);

  // useEffect to handle URL hash changes for displaying class details
  useEffect(() => {
    const handleHashChange = () => {
      const hash = window.location.hash;
      // Check if the hash contains '#class-details?name='
      if (hash.startsWith('#class-details?name=')) {
        const queryString = hash.includes('?') ? hash.split('?')[1] : '';
        const params = new URLSearchParams(queryString);
        const name = params.get('name');
        if (name) {
          const foundClass = imageData.find(item => item.classKey === name);
          setSelectedClassContent(foundClass ? foundClass.content : null);
        } else {
          setSelectedClassContent(null);
        }
      } else {
        setSelectedClassContent(null); // Clear content if hash is not relevant
      }
    };
  
    // Initial check on component mount
    handleHashChange();
  
    // Listen for hash changes
    window.addEventListener('hashchange', handleHashChange);
  
    return () => {
      window.removeEventListener('hashchange', handleHashChange);
    };
  }, [imageData]); // Depend on imageData to ensure it's up-to-date

  const handleClassClick = (classKey) => {
    // Update the URL hash
    window.location.hash = `#class-details?name=${encodeURIComponent(classKey)}`;
    // The useEffect for hashchange will handle setting the content
  };

  return (
    <>
    <Helmet>
        <title>Our Classes | Kids, Adult, Fitness, Online & Private Lessons in Salem</title>
        <meta name="description" content="Explore our diverse range of classes in Salem, including kids' dance and art, adult workshops, fitness programs, convenient online classes, and personalized private lessons. Find the perfect class for you!" />
        <meta name="keywords" content="Our Classes Salem, Kids Classes Salem, Adult Classes Salem, Fitness Classes Salem, Online Classes Salem, Private Classes Salem, Dance Classes Salem, Art Classes Salem, Kids Dance Salem, Adult Dance Salem, Kids Art Salem, Adult Art Salem, Fitness Programs Salem, Online Dance Classes, Online Art Classes, Private Dance Lessons Salem, Private Art Lessons Salem, Group Fitness Salem, Children's Classes Salem, Youth Programs Salem, Beginner Dance Salem, Advanced Dance Salem, Dance Workshops Salem, Art Workshops Salem, Studio Classes Salem, Learn Dance Salem, Learn Art Salem, Fitness Training Salem, Virtual Classes Salem, Personalized Training Salem, One-on-One Classes Salem, Best Kids Classes Salem, Top Adult Classes Salem, Salem Fitness Studio, Salem Online Learning, Salem Private Coaching" />
      </Helmet>
      <style jsx>{`
        .section-wrapper {
          background: linear-gradient(180deg, rgb(26, 24, 24, 1) 0%);
          padding: 20px 20px 60px 20px;
          display: flex;
          flex-direction: column;
          align-items: center;
          position: relative;
          overflow: hidden;
        }

        .section-heading {
          color: #fff;
          font-family: "Impact", "Arial Black", sans-serif;
          font-size: 3rem;
          letter-spacing: 0.05em;
          text-transform: uppercase;
          margin-bottom: 42px;
          text-align: center;
          z-index: 2;
          position: relative;
        }

        .menu-section-container {
          display: flex;
          justify-content: center;
          align-items: stretch;
          gap: 20px;
          flex-wrap: wrap;
          width: 100%;
          max-width: 1400px;
          z-index: 2;
          position: relative;
        }

        .Classs {
          position: relative;
          width: calc(33.333% - 20px);
          min-width: 200px;
          max-width: 250px;
          height: 300px;
          overflow: hidden;
          cursor: pointer;
          transition: transform 0.28s ease, box-shadow 0.28s ease;
          display: flex;
          flex-direction: column;
          justify-content: flex-end;
          align-items: flex-start;
          text-align: left;
          border-radius: 8px;
          box-shadow: 0 6px 20px rgba(0, 0, 0, 0.35);
          text-decoration: none;
        }

        .Classs:hover {
          transform: translateY(-8px) scale(1.01);
          box-shadow: 0 14px 30px rgba(0, 0, 0, 0.45);
        }

        .Classs img {
          width: 100%;
          height: 100%;
          object-fit: cover;
          filter: brightness(0.55) saturate(1.05);
          transition: filter 0.35s ease;
          position: absolute;
          top: 0;
          left: 0;
        }

        .Classs:hover img {
          filter: brightness(1) saturate(1.05);
        }

        .star-icon {
          position: absolute;
          top: 14px;
          right: 14px;
          color: #ffd966;
          font-size: 28px;
          z-index: 3;
          pointer-events: none;
          text-shadow: 0 2px 6px rgba(0, 0, 0, 0.6);
        }

        .image-text {
          position: relative;
          color: white;
          font-family: "Impact", "Arial Black", sans-serif;
          font-size: 1.3rem;
          letter-spacing: 0.05em;
          text-transform: uppercase;
          margin-bottom: 20px;
          margin-left: 20px;
          z-index: 1;
          pointer-events: none;
          text-shadow: 0 4px 18px rgba(0, 0, 0, 0.6);
        }

        .class-details-section {
          background-color: #333;
          color: white;
          padding: 40px;
          margin-top: 40px;
          border-radius: 8px;
          width: 80%;
          max-width: 800px;
          text-align: center;
          font-family: sans-serif;
          font-size: 1.2rem;
          line-height: 1.6;
          box-shadow: 0 4px 15px rgba(0, 0, 0, 0.5);
        }

        @media (max-width: 1024px) {
          .Classs {
            width: calc(50% - 20px);
          }
        }

        @media (max-width: 768px) {
          .section-heading {
            font-size: 2rem;
            margin-bottom: 24px;
          }

          .menu-section-container {
            padding: 0 10px;
          }

          .Classs {
            width: 100%;
            max-width: none;
          }

          .image-text {
            font-size: 1rem;
            margin-left: 15px;
          }

          .class-details-section {
            width: 95%;
            padding: 20px;
            font-size: 1rem;
          }
        }
      `}</style>

      <div className="section-wrapper">
        <h2 className="section-heading">Our Classes</h2>
        <div className="menu-section-container" ref={containerRef}>
          {imageData.map((item, index) => (
            <div
              key={index}
              className="Classs"
              ref={(el) => (imageCardRefs.current[index] = el)}
              aria-label={item.text}
              onClick={() => handleClassClick(item.classKey)}
            >
              <img src={item.src} alt={item.alt || item.text} />
              <span className="star-icon" aria-hidden>
                ★
              </span>
              <div className="image-text">{item.text}</div>
            </div>
          ))}
        </div>

        {selectedClassContent && (
          <div className="class-details-section">
            <h3>Class Details</h3>
            <p>{selectedClassContent}</p>
          </div>
        )}
      </div>
    </>
  );
}

export default OurStudioClassesSection;